import React, { useState, useEffect, useCallback, useRef } from 'react';
import { colors } from '../styles/styles';
import { getDeck, tossCard } from '../services/deckService';
import { updateThoughtBody } from '../services/thoughtService';

/**
 * DeckView — flashcard review. Flip through the active deck one card at a time,
 * shuffled. Next is primary (button + Right-arrow + Spacebar). Toss is a corner
 * ✕ with a confirm modal. End of a pass offers re-shuffle.
 *
 * Mirrors the prototype at psyche-experiment/flashcard-deck.html.
 * Plan: docs/plans/2026-06-14-001-feat-flashcard-deck-plan.md (U4)
 *
 * State model: `cards` (active deck rows) and `queue` (shuffled ids for this
 * pass) are kept IN SYNC — a toss removes the id from BOTH and does not advance
 * `pos` (the next card slides into the current slot), so there is never a stale
 * queue id pointing at a removed card, and a failed toss restores cleanly.
 */

const ANIM_MS = 260;

// Android haptics: fire a short Vibration-API pulse on advance. Guarded because
// desktop browsers and iOS WebKit don't implement it (on iPhone this is a silent
// no-op — iOS haptics come from the native switch overlaid on the Next button
// instead; see the switch <input> in render).
function haptic(pattern = 15) {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch (e) {
    /* vibrate can throw in some embedded webviews — ignore */
  }
}

// Fisher-Yates
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// The in-progress pass (shuffled queue + position) is persisted so a remount or
// reload RESUMES where the reader was instead of reshuffling from the top. The
// component is otherwise fragile to any mid-sitting remount (e.g. the App's
// `{user && <Routes>}` gate tearing down on a transient auth event), which would
// reset pos to 0 and make cards repeat before the pass ends. sessionStorage
// scopes this to the tab session; the Re-shuffle button clears it to start fresh.
const PASS_KEY = 'psyche.deck.pass';
function readSavedPass() {
  try {
    const raw = sessionStorage.getItem(PASS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && Array.isArray(p.queue) ? p : null;
  } catch (e) {
    return null;
  }
}

const DeckView = () => {
  const [cards, setCards] = useState([]);      // active deck (id + body)
  const [queue, setQueue] = useState([]);      // shuffled ids for this pass (kept in sync with cards)
  const [pos, setPos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exiting, setExiting] = useState(null); // 'toss' | 'next' | null
  const [editing, setEditing] = useState(false); // inline body editor open?
  const [draft, setDraft] = useState('');        // editor text buffer
  const savingRef = useRef(false);               // re-entrancy guard for saveEdit

  // Refs guard async hazards: SEPARATE animation timers for Next and Toss (a
  // shared timer let the toss failure path cancel a Next that started after the
  // toss timer fired, silently dropping the advance), a re-entrancy lock on toss
  // confirm, and a mounted flag so timers don't setState after unmount.
  const nextTimerRef = useRef(null);
  const tossTimerRef = useRef(null);
  const tossingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
      if (tossTimerRef.current) clearTimeout(tossTimerRef.current);
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const deck = await getDeck();
      if (!mountedRef.current) return;
      setCards(deck);

      const deckIds = deck.map(c => c.id);
      const deckIdSet = new Set(deckIds);
      const saved = readSavedPass();

      // Resume an in-progress pass if one is saved AND still overlaps the live
      // deck — keep the saved shuffle order for cards still present, drop ones
      // that were tossed/removed, and append any newly-added cards. Resuming on
      // the saved current card keeps the reader exactly where they were.
      if (saved) {
        const savedSet = new Set(saved.queue);
        const kept = saved.queue.filter(id => deckIdSet.has(id));
        // `kept.length === 0` means the saved pass shares nothing with this deck
        // — a different deck or a different user on the same tab. Don't inherit a
        // foreign position; fall through to a fresh pass at card 1.
        if (kept.length) {
          const added = deckIds.filter(id => !savedSet.has(id));
          const resumeQueue = kept.concat(shuffle(added));
          let pos;
          if (saved.currentId && resumeQueue.includes(saved.currentId)) {
            pos = resumeQueue.indexOf(saved.currentId);   // exact resume
          } else if (saved.currentId == null) {
            pos = resumeQueue.length;                      // pass was finished → end-of-pass
          } else {
            // Current card was tossed/removed: land on the nearest remaining
            // card, never past the end (clamping to length would falsely show
            // "end of pass"). Guard a corrupted non-integer saved.pos → 0.
            const savedPos = Number.isInteger(saved.pos) ? saved.pos : 0;
            pos = Math.min(savedPos, resumeQueue.length - 1);
          }
          setQueue(resumeQueue);
          setPos(pos);
          return;
        }
      }

      // No usable saved pass → fresh shuffled pass.
      setQueue(shuffle(deckIds));
      setPos(0);
    } catch (e) {
      if (mountedRef.current) setError(e.message || 'Failed to load deck');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Persist the in-progress pass so a remount/reload resumes instead of
  // reshuffling. Skip the empty initial state (nothing to resume yet).
  useEffect(() => {
    if (queue.length === 0) return;
    try {
      sessionStorage.setItem(PASS_KEY, JSON.stringify({ queue, pos, currentId: queue[pos] ?? null }));
    } catch (e) {
      /* storage unavailable / quota — resume is best-effort, ignore */
    }
  }, [queue, pos]);

  // Stamp the non-standard `switch` attribute on the overlay <input> via a ref so
  // WebKit renders it as a native iOS switch (JSX props don't reliably emit it).
  const setSwitchAttr = useCallback((el) => {
    if (el) el.setAttribute('switch', '');
  }, []);

  const cardById = useCallback((id) => cards.find(c => c.id === id), [cards]);
  const activeCount = cards.length;
  const currentId = queue[pos];
  const currentCard = currentId ? cardById(currentId) : null;

  // Cards still ahead in this pass, counting the one on screen. Drives the
  // decreasing progress bar: full on card 1, one sliver on the last card, empty
  // at end-of-pass. Clamped so a resume-past-end never yields a negative fill.
  const remaining = Math.max(0, queue.length - pos);
  const remainingPct = queue.length ? (remaining / queue.length) * 100 : 0;

  // Re-fetch the live deck and start a fresh shuffled pass (finding: reshuffle
  // should surface newly auto-added / un-tossed cards, not re-shuffle a stale
  // mount-time snapshot).
  const reshuffle = useCallback(() => {
    if (nextTimerRef.current) { clearTimeout(nextTimerRef.current); nextTimerRef.current = null; }
    if (tossTimerRef.current) { clearTimeout(tossTimerRef.current); tossTimerRef.current = null; }
    // Clear the saved pass so load() starts a genuinely fresh shuffle rather
    // than resuming the pass we're deliberately ending.
    try { sessionStorage.removeItem(PASS_KEY); } catch (e) { /* ignore */ }
    setExiting(null);
    load();
  }, [load]);

  const handleNext = useCallback(() => {
    // tossingRef guard: the toss animation timer clears `exiting` at ANIM_MS
    // while the tossCard network call is still in flight. Without this guard,
    // Next could advance `pos` during that window; if the toss then rejects and
    // restores cards/queue, `pos` would point past the restored card (skipped).
    if (exiting || confirmOpen || tossingRef.current || !currentCard) return;
    haptic();
    setExiting('next');
    nextTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setPos(p => p + 1);
      setExiting(null);
      nextTimerRef.current = null;
    }, ANIM_MS);
  }, [exiting, confirmOpen, currentCard]);

  const handleConfirmToss = useCallback(async () => {
    if (tossingRef.current) return;       // re-entry guard (Enter held / double-confirm)
    const id = currentId;
    if (!id) { setConfirmOpen(false); return; }
    tossingRef.current = true;
    setConfirmOpen(false);

    // Snapshot enough to fully restore on failure.
    const cardsSnapshot = cards;
    const queueSnapshot = queue;

    setExiting('toss');
    tossTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      // Remove the id from BOTH cards and queue; pos stays (next card slides in).
      setCards(prev => prev.filter(c => c.id !== id));
      setQueue(prev => prev.filter(qid => qid !== id));
      setExiting(null);
      tossTimerRef.current = null;
    }, ANIM_MS);

    try {
      await tossCard(id);
    } catch (e) {
      if (!mountedRef.current) return;
      // Cancel the pending removal (fast-reject) or undo it (slow-reject), then
      // restore. Only clears the TOSS timer — never a Next timer that may have
      // started in the meantime.
      if (tossTimerRef.current) { clearTimeout(tossTimerRef.current); tossTimerRef.current = null; }
      setCards(cardsSnapshot);
      setQueue(queueSnapshot);
      setExiting(null);
      setError('Could not toss that card — it is still in your deck.');
    } finally {
      tossingRef.current = false;
    }
  }, [currentId, cards, queue]);

  // ── inline body editor ──────────────────────────────────────────
  // Tap the card text to edit the thought's body in place. Only the body is
  // persisted (updateThoughtBody), so no other field is touched.
  const startEdit = useCallback(() => {
    // Never open mid-animation or while the toss modal is up — those own the card.
    if (exiting || confirmOpen || editing || !currentCard) return;
    setError(null);
    setDraft(currentCard.body);
    setEditing(true);
  }, [exiting, confirmOpen, editing, currentCard]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraft('');
  }, []);

  // Autofocus the editor and drop the caret at the end when it mounts.
  const focusEditEnd = useCallback((el) => {
    if (el) { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); }
  }, []);

  const saveEdit = useCallback(async () => {
    if (savingRef.current || !currentCard) return;
    const next = draft.trim();
    // No-op guard: an empty or unchanged save is just a cancel — never write a
    // blank body, never burn an update/re-embed on identical text.
    if (!next || next === currentCard.body) { cancelEdit(); return; }

    savingRef.current = true;
    const id = currentCard.id;
    try {
      await updateThoughtBody(id, next);
      if (!mountedRef.current) return;
      // Update by id (never by pos) so a concurrent advance/toss can't stamp the
      // wrong card. queue/pos are untouched — the pass is unaffected.
      setCards(prev => prev.map(c => (c.id === id ? { ...c, body: next } : c)));
      cancelEdit();
    } catch (e) {
      if (!mountedRef.current) return;
      // Revert: cards was never optimistically changed, so exiting edit mode
      // restores the original text. Surface the failure and keep the deck usable.
      setError('Could not save your edit — the thought was not changed.');
      cancelEdit();
    } finally {
      savingRef.current = false;
    }
  }, [draft, currentCard, cancelEdit]);

  // Keyboard: Next = → / Space; modal = Enter confirm, Esc cancel
  useEffect(() => {
    const onKey = (e) => {
      // While editing, the textarea owns the keyboard — Space/→ must type, not
      // advance the card. (Esc-to-cancel is handled on the textarea itself.)
      if (editing) return;
      if (confirmOpen) {
        if (e.key === 'Escape') setConfirmOpen(false);
        else if (e.key === 'Enter') handleConfirmToss();
        return;
      }
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); handleNext(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editing, confirmOpen, handleNext, handleConfirmToss]);

  // ── render helpers ──────────────────────────────────────────────
  const wrap = {
    boxSizing: 'border-box',
    minHeight: 'calc(100vh - 80px)',
    // `/deck` is a direct route in App.js — it is NOT wrapped in AppContent's
    // container, so it does not inherit the 60px top padding that clears the
    // fixed AppHeader. Without this the header sits on top of the deck's top row
    // (the progress bar + counter). Match AppHeader's height (60px) here.
    paddingTop: 60,
    display: 'flex',
    flexDirection: 'column',
    color: colors.textPrimary,
  };
  const headerRow = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '4px 24px 8px', fontSize: 12, color: colors.textTertiary,
    fontVariantNumeric: 'tabular-nums',
  };
  // Slim track + fill for the decreasing "cards left" bar. The fill width shrinks
  // as `pos` advances; the transition matches the card slide (ANIM_MS) so bar and
  // card move together on each Next/Toss.
  const progressTrack = {
    height: 4, margin: '0 24px 16px', borderRadius: 999,
    background: colors.divider, overflow: 'hidden',
  };
  const progressFill = (pct) => ({
    height: '100%', width: `${pct}%`, background: colors.primary,
    borderRadius: 999, transition: `width ${ANIM_MS}ms cubic-bezier(0.4,0,0.2,1)`,
  });
  const stage = { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 };
  const errorBanner = (msg) => (
    <p style={{
      margin: '0 24px 12px', padding: '8px 12px', background: 'rgba(239,68,68,0.10)',
      border: '1px solid rgba(239,68,68,0.30)', borderRadius: 6, color: '#ef4444', fontSize: 13,
    }}>{msg}</p>
  );

  const cardStyle = (anim) => ({
    width: '100%', maxWidth: 600, minHeight: 340, background: colors.surfaceElevated,
    border: `1px solid ${colors.divider}`, borderRadius: 20, padding: '48px 44px',
    display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative',
    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
    transition: 'transform 0.26s cubic-bezier(0.4,0,0.2,1), opacity 0.26s',
    transform: anim === 'toss' ? 'translateX(-120%) rotate(-12deg)'
             : anim === 'next' ? 'translateX(120%) rotate(8deg)' : 'none',
    opacity: anim ? 0 : 1,
  });

  if (loading) return <div style={wrap}><div style={stage}><span style={{ color: colors.textTertiary }}>Loading deck…</span></div></div>;
  if (error && !cards.length) return <div style={wrap}><div style={stage}><span style={{ color: '#ef4444' }}>{error}</span></div></div>;

  // Empty deck
  if (activeCount === 0) {
    return (
      <div style={wrap}>
        <div style={stage}>
          <div style={{ textAlign: 'center', maxWidth: 460, color: colors.textSecondary }}>
            <h2 style={{ color: colors.textPrimary, fontSize: 22, margin: '0 0 12px' }}>Deck empty</h2>
            <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
              You've tossed every card. New thoughts rated 4-5 land here automatically,
              or add any thought to the deck from its detail page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // End of pass
  if (pos >= queue.length) {
    return (
      <div style={wrap}>
        <div style={headerRow}><span /><span><strong style={{ color: colors.textPrimary }}>{activeCount}</strong> in deck</span></div>
        {error && errorBanner(error)}
        <div style={stage}>
          <div style={{ textAlign: 'center', maxWidth: 460 }}>
            <h2 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 12px', letterSpacing: '-0.01em' }}>End of the pass</h2>
            <p style={{ color: colors.textSecondary, fontSize: 15, margin: '0 0 28px', lineHeight: 1.6 }}>
              <strong style={{ color: colors.textPrimary }}>{activeCount}</strong> still in the deck.
            </p>
            <button onClick={reshuffle} style={{
              background: colors.primary, color: '#0C0C0F', border: 'none', borderRadius: 12,
              padding: '14px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}>Re-shuffle &amp; go again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={headerRow}>
        <span data-testid="deck-progress"><strong style={{ color: colors.textPrimary }}>{pos + 1}</strong> / {queue.length}</span>
        <span>{remaining} left</span>
      </div>
      <div
        role="progressbar"
        aria-label="Cards left in this pass"
        aria-valuemin={0}
        aria-valuemax={queue.length}
        aria-valuenow={remaining}
        data-testid="deck-progress-bar"
        style={progressTrack}
      >
        <div style={progressFill(remainingPct)} />
      </div>
      {error && errorBanner(error)}

      <div style={stage}>
        {currentCard && (
          <div style={cardStyle(exiting)}>
            {editing ? (
              <>
                <textarea
                  ref={focusEditEnd}
                  data-testid="deck-edit"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); } }}
                  style={{
                    width: '100%', minHeight: 200, background: 'transparent', color: colors.textPrimary,
                    border: 'none', outline: 'none', resize: 'none', padding: 0,
                    fontSize: 15, lineHeight: 1.5, fontWeight: 450, fontFamily: 'inherit',
                  }}
                />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
                  <button onClick={cancelEdit} style={{
                    background: 'transparent', border: `1px solid ${colors.divider}`, color: colors.textPrimary,
                    borderRadius: 10, padding: '10px 18px', fontSize: 14, cursor: 'pointer',
                  }}>Cancel</button>
                  <button onClick={saveEdit} style={{
                    background: colors.primary, border: 'none', color: '#0C0C0F',
                    borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  }}>Save</button>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => { if (!exiting) { setError(null); setConfirmOpen(true); } }}
                  disabled={!!exiting}
                  title="Toss this card"
                  style={{
                    position: 'absolute', top: 16, right: 16, width: 30, height: 30, borderRadius: 8,
                    border: 'none', background: 'transparent', color: colors.textTertiary,
                    fontSize: 16, cursor: exiting ? 'default' : 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                  }}
                >✕</button>
                <div
                  data-testid="deck-card"
                  onClick={startEdit}
                  title="Tap to edit"
                  style={{
                    fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap', fontWeight: 450,
                    cursor: exiting ? 'default' : 'text',
                  }}
                >
                  {currentCard.body}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {!editing && (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 24px 40px' }}>
        {/* Next control. A real native iOS <input type="checkbox" switch> is
            overlaid invisibly (opacity 0) on top of the visible button: on iPhone,
            the user's physical tap lands on the switch and fires the Taptic Engine
            (WebKit has no Vibration API, and iOS ≥26.5 ignores programmatic toggles,
            so a genuine tap on a real switch is the only pure-web path to a haptic).
            Its onChange drives the advance + the Android vibrate. The button behind
            keeps keyboard/desktop and non-iOS taps working. */}
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <button onClick={handleNext} style={{
            border: `1px solid ${colors.divider}`, background: colors.surfaceElevated, color: colors.textPrimary,
            borderRadius: 12, padding: '14px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
            minWidth: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            Next <span style={{ fontSize: 11, opacity: 0.6, fontWeight: 400, border: '1px solid currentColor', borderRadius: 4, padding: '0 5px' }}>→</span>
          </button>
          <input
            ref={setSwitchAttr}
            type="checkbox"
            aria-hidden="true"
            tabIndex={-1}
            onChange={handleNext}
            title="Next"
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              margin: 0, opacity: 0, cursor: 'pointer',
            }}
          />
        </div>
      </div>
      )}

      {confirmOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmOpen(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50, backdropFilter: 'blur(2px)',
          }}
        >
          <div style={{
            background: colors.surfaceElevated, border: `1px solid ${colors.divider}`, borderRadius: 16,
            padding: 28, maxWidth: 380, width: 'calc(100% - 48px)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>Toss this card?</h3>
            <p style={{ margin: '0 0 22px', color: colors.textSecondary, fontSize: 14, lineHeight: 1.5 }}>
              It leaves your deck. The thought itself is untouched — still in your journal. You can add it back from its detail page later.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmOpen(false)} style={{
                background: 'transparent', border: `1px solid ${colors.divider}`, color: colors.textPrimary,
                borderRadius: 10, padding: '10px 18px', fontSize: 14, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleConfirmToss} style={{
                background: '#ef4444', border: 'none', color: '#fff', borderRadius: 10,
                padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}>Toss it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckView;
